import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Box, Paper, Popper, Typography } from '@mui/material';
import {
  AccountTree,
  AutoAwesome,
  Checklist,
  Code,
  DataObject,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule,
  Image as ImageIcon,
  Notes,
  ShortText,
  TableChart,
  TextFields,
  Title,
} from '@mui/icons-material';

const ICONS = {
  ai: AutoAwesome,
  field: Notes,
  fieldInline: ShortText,
  variable: DataObject,
  text: TextFields,
  h1: Title,
  h2: Title,
  h3: Title,
  bullets: FormatListBulleted,
  numbers: FormatListNumbered,
  tasks: Checklist,
  quote: FormatQuote,
  code: Code,
  divider: HorizontalRule,
  table: TableChart,
  diagram: AccountTree,
  image: ImageIcon,
};

const EMPTY_SNAPSHOT = { open: false, items: [], selectedIndex: 0, clientRect: null, command: null };

/**
 * Floating "/" command menu. Reads the slash extension's external store so it re-renders
 * only when the menu changes, never on ordinary typing.
 * @param {{editor: import('@tiptap/core').Editor}} props - Props.
 * @returns {JSX.Element|null}
 */
const SlashMenu = ({ editor }) => {
  const store = editor?.storage?.slashCommand?.store;
  const subscribe = useMemo(() => (store ? store.subscribe : () => () => {}), [store]);
  const getSnapshot = useMemo(() => (store ? store.getSnapshot : () => EMPTY_SNAPSHOT), [store]);
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const listRef = useRef(null);

  const { clientRect } = snap;
  const anchor = useMemo(() => {
    if (!clientRect) return null;
    return { getBoundingClientRect: () => clientRect() || new DOMRect(0, 0, 0, 0) };
  }, [clientRect]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${snap.selectedIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [snap.selectedIndex]);

  if (!snap.open || !anchor) return null;

  let lastGroup = null;
  return (
    <Popper open anchorEl={anchor} placement="bottom-start" sx={{ zIndex: 1400 }} modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}>
      <Paper
        ref={listRef}
        role="listbox"
        aria-label="Insert block"
        onMouseDown={(e) => e.preventDefault()}
        sx={{
          width: 300,
          maxHeight: 360,
          overflowY: 'auto',
          py: 0.5,
          borderRadius: 'var(--q-radius-lg, 12px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 28px rgba(28,27,25,0.16)',
        }}
      >
        {snap.items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            No matching blocks
          </Typography>
        )}
        {snap.items.map((item, index) => {
          const Icon = ICONS[item.icon] || TextFields;
          const header = item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const selected = index === snap.selectedIndex;
          return (
            <React.Fragment key={item.id}>
              {header && (
                <Typography
                  component="div"
                  sx={{ px: 1.5, pt: index === 0 ? 0.5 : 1, pb: 0.25, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}
                >
                  {header}
                </Typography>
              )}
              <Box
                role="option"
                aria-selected={selected}
                data-index={index}
                onClick={() => snap.command?.(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  mx: 0.5,
                  px: 1,
                  py: 0.75,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: selected ? 'var(--q-accent-soft, #ECE8FC)' : 'transparent',
                  '&:hover': { bgcolor: selected ? 'var(--q-accent-soft, #ECE8FC)' : 'action.hover' },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    flexShrink: 0,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: item.icon === 'ai' || item.icon === 'field' || item.icon === 'fieldInline' ? 'var(--q-accent, #4F3BD6)' : 'text.secondary',
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>{item.title}</Typography>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{item.description}</Typography>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          );
        })}
      </Paper>
    </Popper>
  );
};

export default SlashMenu;
